import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import "../module_mechanics/Styles/FormList.css";
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner, faEye, faTimesCircle, 
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, 
    faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faFilter, faEyeSlash,
    faFileInvoice, faImage, faCamera, faUpload, faClock,
    faHourglassHalf, faCheck, faMinusCircle, faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ============================================
// SHARED TRANSLATOR FOR PENDING INVOICES LIST
// ============================================
const createPendingInvoicesTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_pending_invoices_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_pending_invoices_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Pending Invoices': 'زیر التواء انوائسز',
                'All pending invoices are below': 'تمام زیر التواء انوائسز نیچے ہیں',
                'Invoice Number': 'انوائس نمبر',
                'Customer Name': 'کسٹمر کا نام',
                'Bill Number': 'بل نمبر',
                'Bill Pictures': 'بل کی تصاویر',
                'Status': 'حالت',
                'Actions': 'اعمال',
                'Added By': 'شامل کرنے والا',
                'Search invoices...': 'انوائسز تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'Edit': 'ترمیم کریں',
                'Close': 'بند کریں',
                'Save Changes': 'تبدیلیاں محفوظ کریں',
                'Cancel': 'منسوخ کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Showing': 'دکھا رہا ہے',
                'of': 'میں سے',
                'Previous': 'پچھلا',
                'Next': 'اگلا',
                'All': 'سب',
                'Invoice Details': 'انوائس کی تفصیلات',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view invoices': 'براہ کرم انوائسز دیکھنے کے لیے شاپ منتخب کریں',
                'S.No': 'نمبر',
                'Invoices': 'انوائسز',
                'Shop': 'شاپ',
                'View': 'دیکھیں',
                'Update Status': 'حالت اپڈیٹ کریں',
                'Updating...': 'اپڈیٹ ہو رہا ہے...',
                'Status updated successfully': 'حالت کامیابی سے اپڈیٹ ہو گئی',
                'Error updating status': 'حالت اپڈیٹ کرنے میں خرابی',
                'Pending': 'زیر التواء',
                'Approved': 'منظور شدہ',
                'Rejected': 'مسترد شدہ',
                'Processing': 'پروسیسنگ',
                'Completed': 'مکمل',
                'Invoice Report': 'انوائس رپورٹ',
                'Total Invoices': 'کل انوائسز',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'FILTERS APPLIED': 'فلٹرز لاگو ہیں',
                'Search': 'تلاش کریں',
                'From': 'سے',
                'To': 'تک',
                'Page': 'صفحہ',
                'Created At': 'تخلیق کی تاریخ',
                'Updated At': 'اپڈیٹ کی تاریخ',
                'Invoice ID': 'انوائس آئی ڈی',
                'Pictures': 'تصاویر',
                'No pictures': 'کوئی تصاویر نہیں',
                'to': 'تک',
                'per page': 'فی صفحہ',
                'Show': 'دکھائیں',
                'Status Filter': 'حالت فلٹر',
                'All Statuses': 'تمام حالتیں',
                'Edit Invoice': 'انوائس میں ترمیم کریں',
                'Save Status': 'حالت محفوظ کریں'
            },
            'ar': {
                'Pending Invoices': 'الفواتير المعلقة',
                'All pending invoices are below': 'جميع الفواتير المعلقة أدناه',
                'Invoice Number': 'رقم الفاتورة',
                'Customer Name': 'اسم العميل',
                'Bill Number': 'رقم الفاتورة',
                'Bill Pictures': 'صور الفاتورة',
                'Status': 'الحالة',
                'Actions': 'الإجراءات',
                'Added By': 'أضيف بواسطة',
                'Search invoices...': 'بحث عن فواتير...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'Edit': 'تعديل',
                'Close': 'إغلاق',
                'Save Changes': 'حفظ التغييرات',
                'Cancel': 'إلغاء',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Showing': 'عرض',
                'of': 'من',
                'Previous': 'السابق',
                'Next': 'التالي',
                'All': 'الكل',
                'Invoice Details': 'تفاصيل الفاتورة',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view invoices': 'الرجاء اختيار متجر لعرض الفواتير',
                'S.No': 'رقم',
                'Invoices': 'الفواتير',
                'Shop': 'متجر',
                'View': 'عرض',
                'Update Status': 'تحديث الحالة',
                'Updating...': 'جاري التحديث...',
                'Status updated successfully': 'تم تحديث الحالة بنجاح',
                'Error updating status': 'خطأ في تحديث الحالة',
                'Pending': 'معلقة',
                'Approved': 'معتمدة',
                'Rejected': 'مرفوضة',
                'Processing': 'قيد المعالجة',
                'Completed': 'مكتملة',
                'Invoice Report': 'تقرير الفواتير',
                'Total Invoices': 'إجمالي الفواتير',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'FILTERS APPLIED': 'المرشحات المطبقة',
                'Search': 'بحث',
                'From': 'من',
                'To': 'إلى',
                'Page': 'صفحة',
                'Created At': 'تاريخ الإنشاء',
                'Updated At': 'تاريخ التحديث',
                'Invoice ID': 'معرف الفاتورة',
                'Pictures': 'الصور',
                'No pictures': 'لا توجد صور',
                'to': 'إلى',
                'per page': 'لكل صفحة',
                'Show': 'عرض',
                'Status Filter': 'فلتر الحالة',
                'All Statuses': 'جميع الحالات',
                'Edit Invoice': 'تعديل الفاتورة',
                'Save Status': 'حفظ الحالة'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) { }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        pendingInvoices: 'Pending Invoices',
        formDetails: 'All pending invoices are below',
        invoices: 'Invoices',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search invoices...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your invoice data...',
        selectShop: 'Please select a shop to view invoices',
        invoiceNumber: 'Invoice Number',
        customerName: 'Customer Name',
        billNumber: 'Bill Number',
        billPictures: 'Bill Pictures',
        status: 'Status',
        actions: 'Actions',
        addedBy: 'Added By',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        invoiceNumberFilter: 'Invoice Number',
        customerNameFilter: 'Customer Name',
        statusFilter: 'Status Filter',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        invoiceDetails: 'Invoice Details',
        editInvoice: 'Edit Invoice',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        invoiceReport: 'Invoice Report',
        totalInvoices: 'Total Invoices',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        created_at: 'Created At',
        updated_at: 'Updated At',
        invoiceId: 'Invoice ID',
        pictures: 'Pictures',
        noPictures: 'No pictures',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        processing: 'Processing',
        completed: 'Completed',
        updateStatus: 'Update Status',
        updating: 'Updating...',
        statusUpdated: 'Status updated successfully',
        errorUpdatingStatus: 'Error updating status',
        saveStatus: 'Save Status',
        allStatuses: 'All Statuses',
        show: 'Show',
        perPage: 'per page'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const pendingInvoicesTranslator = createPendingInvoicesTranslator();

// ============================================
// API ENDPOINT
// ============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/solar_module';
const INVOICE_API = `${API_BASE}/solar_pendinginvoices.php`;

const compressBillImage = (file, maxSize = 1200, quality = 0.75) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
        const image = new Image();
        image.onerror = reject;
        image.onload = () => {
            const ratio = Math.min(1, maxSize / image.width, maxSize / image.height);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(image.width * ratio);
            canvas.height = Math.round(image.height * ratio);
            canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        image.src = reader.result;
    };
    reader.readAsDataURL(file);
});

// ============================================
// Toast Notification Component
// ============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};
// ============================================
// Shop Selector Modal Component
// ============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozlistui-modal-overlay" onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// Custom Dropdown Component
// ============================================
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: label } });
            window.dispatchEvent(event);
        }
    }, [isOpen, label]);

    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== label) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [label]);

    const filteredOptions = isHovering
        ? options
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div
            className="ozlistui-custom-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ozlistui-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// Status Badge Component
// ============================================
const StatusBadge = ({ status, translations }) => {
    const getStatusConfig = (status) => {
        const statusMap = {
            'pending': { 
                icon: faClock, 
                color: '#f59e0b', 
                bg: '#fef3c7',
                label: translations?.pending || 'Pending'
            },
            'approved': { 
                icon: faCheckCircle, 
                color: '#10b981', 
                bg: '#d1fae5',
                label: translations?.approved || 'Approved'
            },
            'rejected': { 
                icon: faTimesCircle, 
                color: '#ef4444', 
                bg: '#fecaca',
                label: translations?.rejected || 'Rejected'
            },
            'processing': { 
                icon: faSpinner, 
                color: '#3b82f6', 
                bg: '#dbeafe',
                label: translations?.processing || 'Processing'
            },
            'completed': { 
                icon: faCheck, 
                color: '#8b5cf6', 
                bg: '#ede9fe',
                label: translations?.completed || 'Completed'
            }
        };
        return statusMap[status?.toLowerCase()] || statusMap['pending'];
    };

    const config = getStatusConfig(status);

    return (
        <span className="ozlistui-status-badge" style={{
            backgroundColor: config.bg,
            color: config.color,
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap'
        }}>
            <FontAwesomeIcon icon={config.icon} style={{ fontSize: '11px' }} />
            {config.label}
        </span>
    );
};

// ============================================
// Main Pending Invoices List Component
// ============================================
const PendingInvoicesList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Status filter state
    const [statusFilter, setStatusFilter] = useState('');

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Edit form state
    const [editStatus, setEditStatus] = useState('');
    const [editBillNumber, setEditBillNumber] = useState('');
    const [editBillPictures, setEditBillPictures] = useState([]);
    const editPicturesInputRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    // Status filter options
    const statusOptions = useMemo(() => [
        { value: '', label: translations.allStatuses || 'All Statuses' },
        { value: 'pending', label: translations.pending || 'Pending' },
        { value: 'approved', label: translations.approved || 'Approved' },
        { value: 'rejected', label: translations.rejected || 'Rejected' },
        { value: 'processing', label: translations.processing || 'Processing' },
        { value: 'completed', label: translations.completed || 'Completed' }
    ], [translations]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============================================
    // THEME SYNC
    // ============================================
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
            document.documentElement.style.setProperty('--ozlistui-primary', color);
            document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
            document.documentElement.style.setProperty('--ozlistui-primary-light', color);
            setCurrentTheme(color);
        };

        applyTheme(currentTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);

            const container = document.querySelector('.ozlistui-container');
            if (container) {
                if (isDark) {
                    container.classList.add('ozlistui-dark');
                } else {
                    container.classList.remove('ozlistui-dark');
                }
            }

            if (isDark) {
                document.body.classList.add('ozlistui-dark');
                document.documentElement.classList.add('ozlistui-dark');
            } else {
                document.body.classList.remove('ozlistui-dark');
                document.documentElement.classList.remove('ozlistui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await pendingInvoicesTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozlistui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ============================================
    // FETCH INVOICES
    // ============================================
    const fetchInvoices = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch(INVOICE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    limit: 10000,
                    offset: 0
                })
            });
            
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error("Server returned non-JSON response:", text);
                throw new Error('Server returned non-JSON response');
            }
            
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setInvoices(data.data || []);
                    setStatusFilter('');
                } else {
                    showToast('error', data.message || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            if (isMountedRef.current) showToast('error', 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, translations, showToast]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || '',
                        bussinesslogo: '',
                        businessaddress: '',
                        fullname: user.fullname || '',
                        email: user.email || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        }
    }, [user]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchInvoices();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                setShowShopSelector(false);
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast]);

    // ============================================
    // APPLY FILTERS
    // ============================================
    const applyFilters = useCallback(() => {
        let filtered = [...invoices];

        // Search term filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(invoice =>
                invoice.invoice_number?.toLowerCase().includes(term) ||
                invoice.customer_name?.toLowerCase().includes(term) ||
                invoice.bill_number?.toLowerCase().includes(term) ||
                invoice.username?.toLowerCase().includes(term)
            );
        }

        // Status filter
        if (statusFilter) {
            filtered = filtered.filter(invoice => 
                invoice.status?.toLowerCase() === statusFilter.toLowerCase()
            );
        }

        setFilteredInvoices(filtered);
        setCurrentPage(1);
    }, [invoices, searchTerm, statusFilter]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    const resetFilters = useCallback(() => {
        setStatusFilter('');
        setSearchTerm('');
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    };

    const currentDateGlobal = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    };

    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') {
            return 18;
        }
        return 12;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentInvoices = filteredInvoices.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentInvoices };
    }, [filteredInvoices, currentPage, itemsPerPage]);

    // ============================================
    // OPEN PICTURE IN NEW TAB
    // ============================================
   // ============================================
// OPEN PICTURE IN NEW TAB
// ============================================
// ============================================
// OPEN PICTURE IN NEW TAB
// ============================================
const openPicturesInNewTab = useCallback((invoice) => {
    const pictures = invoice.bill_pictures;
    if (!pictures || !Array.isArray(pictures) || pictures.length === 0) {
        showToast('warning', translations.noPictures || 'No pictures available');
        return;
    }

    // Create gallery thumbnails
    const imageTags = pictures.map((pic, idx) => {
        return `
            <div class="image-container" onclick="openFullscreen(${idx})">
                <img src="${pic}" alt="Bill picture ${idx + 1}" onerror="this.parentElement.innerHTML='<div class=\\'error-message\\'>Image ${idx + 1} could not be loaded</div>'" />
                <div class="caption">Picture ${idx + 1} of ${pictures.length}</div>
            </div>
        `;
    }).join('');

    // Create fullscreen viewer HTML
    const fullscreenViewer = `
        <div id="fullscreen-overlay" class="fullscreen-overlay" style="display:none;" onclick="closeFullscreen()">
            <div class="fullscreen-content" onclick="event.stopPropagation()">
                <button class="fullscreen-close" onclick="closeFullscreen()">✕</button>
                <button class="fullscreen-nav fullscreen-prev" onclick="prevImage(event)">‹</button>
                <img id="fullscreen-image" src="" alt="Fullscreen image" />
                <button class="fullscreen-nav fullscreen-next" onclick="nextImage(event)">›</button>
                <div class="fullscreen-counter" id="fullscreen-counter">1 / ${pictures.length}</div>
                <div class="fullscreen-hint">Use arrow keys to navigate • ESC to close</div>
            </div>
        </div>
    `;

    const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Bill Pictures - ${invoice.invoice_number || 'Invoice'}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                body {
                    padding: 0;
                    background: #f5f7fa;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
                    min-height: 100vh;
                }
                
                /* Main Container - Full Width */
                .container {
                    width: 100%;
                    max-width: 100%;
                    margin: 0;
                    padding: 0;
                }
                
                /* Header - Full Width, No Border Radius */
                .header {
                    background: linear-gradient(135deg, #f8f9fc, #eef1f5);
                    padding: 24px 40px;
                    margin-bottom: 28px;
                    border-bottom: 2px solid #e2e6ea;
                    width: 100%;
                }
                .header-content {
                    max-width: 1400px;
                    margin: 0 auto;
                }
                .header h1 {
                    font-size: 26px;
                    color: #1a1a2e;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    font-weight: 700;
                }
                .header h1 .image-count {
                    display: inline-flex;
                    align-items: center;
                    background: #e8ecf1;
                    color: #4a4a6a;
                    padding: 2px 14px;
                    font-size: 13px;
                    font-weight: 600;
                    margin-left: 8px;
                }
                .header .invoice-info {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 24px;
                    margin-top: 14px;
                    padding-top: 14px;
                    border-top: 1px solid #dce0e5;
                    color: #4a4a6a;
                    font-size: 14px;
                }
                .header .invoice-info .info-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .header .invoice-info .label {
                    color: #8a8aaa;
                    font-weight: 500;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .header .invoice-info .value {
                    color: #1a1a2e;
                    font-weight: 600;
                    font-size: 14px;
                }
                .header .invoice-info .value.status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 3px 14px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: capitalize;
                }
                .status-pending { background: #fef3c7; color: #d97706; }
                .status-approved { background: #d1fae5; color: #059669; }
                .status-rejected { background: #fecaca; color: #dc2626; }
                .status-processing { background: #dbeafe; color: #2563eb; }
                .status-completed { background: #ede9fe; color: #7c3aed; }
                
                /* Gallery Grid */
                .gallery {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 20px;
                    padding: 0 40px 40px 40px;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                .image-container {
                    background: #ffffff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
                    overflow: hidden;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    border: 1px solid #e8ecf1;
                    cursor: pointer;
                }
                .image-container:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
                    border-color: #6366f1;
                }
                .image-container img {
                    width: 100%;
                    height: 340px;
                    object-fit: contain;
                    display: block;
                    background: #fafbfc;
                    padding: 12px;
                }
                .image-container .caption {
                    padding: 12px 16px;
                    text-align: center;
                    color: #6a6a8a;
                    font-size: 13px;
                    background: #fafbfc;
                    border-top: 1px solid #e8ecf1;
                    font-weight: 500;
                }
                .error-message {
                    padding: 80px 20px;
                    text-align: center;
                    color: #dc2626;
                    font-size: 16px;
                }
                
                /* Fullscreen Overlay */
                .fullscreen-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.92);
                    z-index: 9999;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    animation: fadeIn 0.3s ease;
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .fullscreen-content {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .fullscreen-content img {
                    max-width: 90%;
                    max-height: 85%;
                    object-fit: contain;
                    animation: zoomIn 0.3s ease;
                    user-select: none;
                    -webkit-user-select: none;
                }
                @keyframes zoomIn {
                    from { transform: scale(0.95); opacity: 0; }
                    to { transform: scale(1); opacity: 1; }
                }
                
                /* Professional Close Button */
                .fullscreen-close {
                    position: absolute;
                    top: 24px;
                    right: 32px;
                    background: rgba(255,255,255,0.12);
                    color: #fff;
                    border: 1px solid rgba(255,255,255,0.15);
                    font-size: 18px;
                    width: 40px;
                    height: 40px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 300;
                    letter-spacing: 0.5px;
                }
                .fullscreen-close:hover {
                    background: rgba(255,255,255,0.2);
                    border-color: rgba(255,255,255,0.3);
                    transform: scale(1.05);
                }
                
                /* Professional Navigation Buttons */
                .fullscreen-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background: rgba(255,255,255,0.08);
                    color: #fff;
                    border: 1px solid rgba(255,255,255,0.1);
                    width: 44px;
                    height: 44px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    font-weight: 300;
                    user-select: none;
                    -webkit-user-select: none;
                }
                .fullscreen-nav:hover {
                    background: rgba(255,255,255,0.18);
                    border-color: rgba(255,255,255,0.25);
                    transform: translateY(-50%) scale(1.05);
                }
                .fullscreen-nav:active {
                    transform: translateY(-50%) scale(0.95);
                }
                .fullscreen-prev {
                    left: 28px;
                }
                .fullscreen-next {
                    right: 28px;
                }
                
                /* Counter */
                .fullscreen-counter {
                    position: absolute;
                    bottom: 32px;
                    left: 50%;
                    transform: translateX(-50%);
                    color: rgba(255,255,255,0.6);
                    font-size: 14px;
                    background: rgba(0,0,0,0.4);
                    padding: 4px 16px;
                    font-weight: 500;
                    letter-spacing: 0.5px;
                    backdrop-filter: blur(4px);
                    -webkit-backdrop-filter: blur(4px);
                }
                .fullscreen-hint {
                    position: absolute;
                    bottom: 80px;
                    left: 50%;
                    transform: translateX(-50%);
                    color: rgba(255,255,255,0.2);
                    font-size: 12px;
                    white-space: nowrap;
                    font-weight: 300;
                    letter-spacing: 0.3px;
                }
                
                /* Gallery Footer */
                .gallery-footer {
                    padding: 0 40px 40px 40px;
                    text-align: center;
                    max-width: 1400px;
                    margin: 0 auto;
                }
                .close-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 24px;
                    background: #e8ecf1;
                    color: #4a4a6a;
                    border: none;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background 0.2s;
                    font-weight: 500;
                }
                .close-btn:hover {
                    background: #dce0e5;
                }
                
                /* Responsive */
                @media (max-width: 768px) {
                    .header { padding: 16px 20px; }
                    .header h1 { font-size: 20px; flex-wrap: wrap; }
                    .header .invoice-info { flex-direction: column; gap: 8px; }
                    .gallery { padding: 0 16px 20px 16px; grid-template-columns: 1fr; gap: 14px; }
                    .image-container img { height: 240px; padding: 8px; }
                    
                    .fullscreen-nav {
                        width: 36px;
                        height: 36px;
                        font-size: 18px;
                    }
                    .fullscreen-prev { left: 12px; }
                    .fullscreen-next { right: 12px; }
                    .fullscreen-close {
                        top: 16px;
                        right: 16px;
                        width: 36px;
                        height: 36px;
                        font-size: 16px;
                    }
                    .fullscreen-counter { font-size: 12px; padding: 4px 14px; bottom: 24px; }
                    .fullscreen-hint { display: none; }
                    .gallery-footer { padding: 0 16px 20px 16px; }
                }
                @media (max-width: 480px) {
                    .header { padding: 12px 16px; }
                    .header h1 { font-size: 17px; }
                    .header .invoice-info .info-item { flex-wrap: wrap; }
                    .image-container img { height: 180px; padding: 6px; }
                    
                    .fullscreen-nav {
                        width: 30px;
                        height: 30px;
                        font-size: 14px;
                    }
                    .fullscreen-prev { left: 8px; }
                    .fullscreen-next { right: 8px; }
                    .fullscreen-close {
                        top: 12px;
                        right: 12px;
                        width: 30px;
                        height: 30px;
                        font-size: 14px;
                    }
                }
            </style>
        </head>
        <body>
            <div class="container">
                <!-- Header - Full Width, No Border Radius -->
                <div class="header">
                    <div class="header-content">
                        <h1>
                            Bill Pictures
                            <span class="image-count">${pictures.length} ${pictures.length === 1 ? 'image' : 'images'}</span>
                        </h1>
                        <div class="invoice-info">
                            <div class="info-item">
                                <span class="label">Invoice</span>
                                <span class="value">${invoice.invoice_number || 'N/A'}</span>
                            </div>
                            <div class="info-item">
                                <span class="label">Bill Number</span>
                                <span class="value">${invoice.bill_number || 'N/A'}</span>
                            </div>
                            <div class="info-item">
                                <span class="label">Status</span>
                                <span class="value status-badge status-${(invoice.status || 'pending').toLowerCase()}">
                                    ${invoice.status || 'Pending'}
                                </span>
                            </div>
                            <div class="info-item">
                                <span class="label">Added By</span>
                                <span class="value">${invoice.username || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Gallery -->
                <div class="gallery">
                    ${imageTags}
                </div>
                
                <!-- Fullscreen Viewer -->
                ${fullscreenViewer}
                
                <!-- Footer -->
                <div class="gallery-footer">
                    <button class="close-btn" onclick="window.close()">✕ Close Window</button>
                </div>
            </div>

            <script>
                const pictures = ${JSON.stringify(pictures)};
                let currentIndex = 0;
                const overlay = document.getElementById('fullscreen-overlay');
                const fullscreenImage = document.getElementById('fullscreen-image');
                const counter = document.getElementById('fullscreen-counter');

                function openFullscreen(index) {
                    currentIndex = index;
                    updateImage();
                    overlay.style.display = 'flex';
                    document.body.style.overflow = 'hidden';
                }

                function closeFullscreen() {
                    overlay.style.display = 'none';
                    document.body.style.overflow = '';
                }

                function updateImage() {
                    fullscreenImage.src = pictures[currentIndex];
                    counter.textContent = (currentIndex + 1) + ' / ' + pictures.length;
                }

                function prevImage(e) {
                    e.stopPropagation();
                    currentIndex = (currentIndex - 1 + pictures.length) % pictures.length;
                    updateImage();
                }

                function nextImage(e) {
                    e.stopPropagation();
                    currentIndex = (currentIndex + 1) % pictures.length;
                    updateImage();
                }

                // Keyboard navigation
                document.addEventListener('keydown', function(e) {
                    if (overlay.style.display === 'flex') {
                        if (e.key === 'ArrowLeft') {
                            e.preventDefault();
                            currentIndex = (currentIndex - 1 + pictures.length) % pictures.length;
                            updateImage();
                        } else if (e.key === 'ArrowRight') {
                            e.preventDefault();
                            currentIndex = (currentIndex + 1) % pictures.length;
                            updateImage();
                        } else if (e.key === 'Escape') {
                            closeFullscreen();
                        }
                    }
                });

                // Close on click outside
                overlay.addEventListener('click', function(e) {
                    if (e.target === overlay) {
                        closeFullscreen();
                    }
                });
            </script>
        </body>
        </html>
    `;

    // Create a Blob and open in new tab
    const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    
    if (newWindow) {
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 5000);
    } else {
        showToast('error', 'Popup blocked. Please allow popups for this site.');
        URL.revokeObjectURL(url);
    }
}, [translations, showToast]);
    // ============================================
    // VIEW INVOICE
    // ============================================
    const handleView = useCallback((invoice) => {
        setSelectedInvoice(invoice);
        setShowViewModal(true);
    }, []);

    // ============================================
    // EDIT INVOICE STATUS
    // ============================================
    const handleEdit = useCallback((invoice) => {
        // Check if user has permission (they added it)
        if (invoice.username !== user?.username) {
            showToast('error', 'You can only edit invoices you created');
            return;
        }
        setSelectedInvoice(invoice);
        setEditStatus(invoice.status || 'pending');
        setEditBillNumber(invoice.bill_number || '');
        setEditBillPictures(Array.isArray(invoice.bill_pictures) ? [...invoice.bill_pictures] : []);
        setShowEditModal(true);
    }, [user, showToast]);

    const handleEditPicturesUpload = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;

        const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024);
        if (validFiles.length !== files.length) {
            showToast('warning', 'Some pictures were skipped because they are larger than 10MB');
        }

        try {
            const pictures = await Promise.all(validFiles.map(file => compressBillImage(file)));
            setEditBillPictures(previous => [...previous, ...pictures]);
        } catch (error) {
            console.error('Error processing bill pictures:', error);
            showToast('error', 'One or more pictures could not be processed');
        }
    }, [showToast]);

    const removeEditPicture = useCallback((index) => {
        setEditBillPictures(previous => previous.filter((_, pictureIndex) => pictureIndex !== index));
    }, []);

    // ============================================
    // UPDATE STATUS
    // ============================================
    const handleUpdateStatus = useCallback(async () => {
        if (!selectedInvoice || !currentShop) {
            showToast('error', 'Invoice or shop information missing');
            return;
        }
        if (selectedInvoice.username !== user?.username) {
            showToast('error', 'You can only update invoices you created');
            return;
        }

        setEditLoading(true);
        try {
            const payload = {
                action: 'edit',
                id: selectedInvoice.id,
                shop_id: currentShop.shop_id,
                username: user.username,
                invoice_number: selectedInvoice.invoice_number,
                customer_name: selectedInvoice.customer_name || '',
                bill_number: editBillNumber.trim(),
                bill_pictures: editBillPictures,
                status: editStatus
            };

            const response = await fetch(INVOICE_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                const text = await response.text();
                console.error("Server returned non-JSON response:", text);
                throw new Error('Server returned non-JSON response');
            }

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.statusUpdated || 'Status updated successfully');
                fetchInvoices();
                setShowEditModal(false);
                setSelectedInvoice(null);
                setEditStatus('');
                setEditBillNumber('');
                setEditBillPictures([]);
            } else {
                showToast('error', data.message || translations.errorUpdatingStatus || 'Error updating status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showToast('error', error.message || translations.errorUpdatingStatus || 'Error updating status');
        } finally {
            setEditLoading(false);
        }
    }, [selectedInvoice, currentShop, user, editStatus, editBillNumber, editBillPictures, translations, showToast, fetchInvoices]);

    // ============================================
    // DELETE INVOICE
    // ============================================
    const handleDeleteClick = useCallback((invoiceId) => {
        const invoice = invoices.find(i => i.id === invoiceId);
        if (!invoice) return;
        if (invoice.username !== user?.username) {
            showToast('error', 'You can only delete invoices you created');
            return;
        }
        setInvoiceToDelete(invoiceId);
        setShowDeleteModal(true);
    }, [invoices, user, showToast]);

    const handleDelete = useCallback(async () => {
        if (!invoiceToDelete || !currentShop) return;
        const invoice = invoices.find(i => i.id === invoiceToDelete);
        if (!invoice || invoice.username !== user?.username) {
            showToast('error', 'You can only delete your own invoices');
            setShowDeleteModal(false);
            setInvoiceToDelete(null);
            return;
        }
        // Note: You would need a delete action in your API
        // For now, we'll just show a toast
        showToast('warning', 'Delete functionality not implemented in API');
        setShowDeleteModal(false);
        setInvoiceToDelete(null);
    }, [invoiceToDelete, currentShop, invoices, user, showToast]);

    // ============================================
    // EXPORT TO PDF
    // ============================================
    const exportToPDF = useCallback(async () => {
        if (!filteredInvoices || filteredInvoices.length === 0) {
            showToast('warning', translations.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = currentLang === 'ur' || currentLang === 'ar';

            const pdfTextColor = '#1f2937';
            const pdfSubTextColor = '#6b7280';
            const pdfBorderColor = '#e5e7eb';
            const pdfHeaderBgColor = '#f9fafb';
            const pdfRowEvenBg = '#ffffff';
            const pdfRowOddBg = '#f9fafb';
            const pdfThemeColor = currentTheme;

            const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: pageFormat, compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const rowsPerPage = getRowsPerPage();

            let invoicesToExport = [...filteredInvoices];
            const totalPages = Math.ceil(invoicesToExport.length / rowsPerPage);

            const getPDFTranslation = (key, fallback = '') => {
                return translations[key] || fallback;
            };

            const renderPage = async (pageInvoices, pageNum, startRowIndex) => {
                return new Promise((resolve, reject) => {
                    try {
                        const container = document.createElement('div');
                        container.style.width = '1200px';
                        container.style.padding = '12px 15px';
                        container.style.backgroundColor = '#ffffff';
                        container.style.color = pdfTextColor;
                        container.style.position = 'absolute';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
                        container.style.direction = isRTL ? 'rtl' : 'ltr';

                        const reportLabel = getPDFTranslation('invoiceReport', 'Invoice Report');
                        const totalLabel = getPDFTranslation('totalInvoices', 'Total Invoices');
                        const generatedLabel = getPDFTranslation('generated', 'Generated');
                        const reportIdLabel = getPDFTranslation('reportId', 'Report ID');
                        const generatedByLabel = getPDFTranslation('generatedBy', 'Generated by');
                        const shopCodeLabel = getPDFTranslation('shopCode', 'Shop Code');
                        const pageLabel = getPDFTranslation('page', 'Page');
                        const ofLabel = getPDFTranslation('of', 'of');
                        const invoiceNoLabel = getPDFTranslation('invoiceNumber', 'Invoice Number');
                        const customerLabel = getPDFTranslation('customerName', 'Customer Name');
                        const billNoLabel = getPDFTranslation('billNumber', 'Bill Number');
                        const statusLabel = getPDFTranslation('status', 'Status');
                        const addedByLabel = getPDFTranslation('addedBy', 'Added By');
                        const createdLabel = getPDFTranslation('created_at', 'Created At');
                        const picturesLabel = getPDFTranslation('pictures', 'Pictures');

                        const headerHTML = `
                            <div style="margin-bottom: 12px; text-align: center;">
                                <div style="font-size: 8px; letter-spacing: 3px; color: ${pdfSubTextColor}; margin-bottom: 4px; text-transform: uppercase; font-weight: 500;">${reportLabel}</div>
                                <h1 style="color: ${pdfTextColor}; font-size: 20px; margin: 0 0 4px 0; font-weight: 600;">
                                    ${escapeHtml(currentShop?.shop_name) || 'Pending Invoices'}
                                </h1>
                                <div style="width: 40px; height: 2px; background: ${pdfThemeColor}; margin: 6px auto;"></div>
                                ${businessInfo.businessname ? `<div style="color: ${pdfSubTextColor}; font-size: 10px; margin-top: 4px;">${escapeHtml(businessInfo.businessname)}</div>` : ''}
                                <div style="color: ${pdfSubTextColor}; font-size: 7px; margin-top: 4px;">
                                    ${totalLabel}: ${invoicesToExport.length} | ${generatedLabel}: ${new Date().toLocaleString(currentLang)}
                                </div>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin: 8px 0 10px 0; padding: 5px 0; border-top: 1px solid ${pdfBorderColor}; border-bottom: 1px solid ${pdfBorderColor}; ${isRTL ? 'flex-direction: row-reverse;' : ''}">
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${reportIdLabel}:</strong> #${currentDateGlobal().replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${generatedByLabel}:</strong> ${escapeHtml(user?.fullname || user?.username || 'System')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${shopCodeLabel}:</strong> ${escapeHtml(currentShop?.shop_code || 'N/A')}</div>
                            </div>
                        `;

                        let tableHTML = `
                            <table style="width: 100%; border-collapse: collapse; font-size: 5.5px; margin-top: 4px;">
                                <thead>
                                    <tr>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${getPDFTranslation('serialNo', 'S.No')}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: ${isRTL ? 'right' : 'left'}; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${invoiceNoLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: ${isRTL ? 'right' : 'left'}; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${customerLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${billNoLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${statusLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${picturesLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${createdLabel}</span></th>
                                        <th style="padding: 3px 3px; background: ${pdfHeaderBgColor}; color: ${pdfTextColor}; font-weight: 600; text-align: center; border: 0.5px solid ${pdfBorderColor};"><span style="font-size: 5px;">${addedByLabel}</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                        `;

                        pageInvoices.forEach((invoice, idx) => {
                            const rowNum = startRowIndex + idx + 1;
                            const isEven = idx % 2 === 0;

                            const pictureCount = Array.isArray(invoice.bill_pictures) ? invoice.bill_pictures.length : 0;

                            const statusConfig = {
                                'pending': { label: getPDFTranslation('pending', 'Pending'), color: '#f59e0b' },
                                'approved': { label: getPDFTranslation('approved', 'Approved'), color: '#10b981' },
                                'rejected': { label: getPDFTranslation('rejected', 'Rejected'), color: '#ef4444' },
                                'processing': { label: getPDFTranslation('processing', 'Processing'), color: '#3b82f6' },
                                'completed': { label: getPDFTranslation('completed', 'Completed'), color: '#8b5cf6' }
                            };
                            const statusInfo = statusConfig[invoice.status?.toLowerCase()] || { label: invoice.status || '-', color: '#6b7280' };

                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${isEven ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 2px 2px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${rowNum}</td>
                                    <td style="padding: 2px 2px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; font-weight: 500; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(invoice.invoice_number) || '-'}</td>
                                    <td style="padding: 2px 2px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(invoice.customer_name) || '-'}</td>
                                    <td style="padding: 2px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(invoice.bill_number) || '-'}</td>
                                    <td style="padding: 2px 2px; text-align: center; border-right: 0.5px solid ${pdfBorderColor};">
                                        <span style="background: ${statusInfo.color}20; color: ${statusInfo.color}; padding: 2px 8px; border-radius: 10px; font-size: 4.5px; font-weight: 500;">${statusInfo.label}</span>
                                    </td>
                                    <td style="padding: 2px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${pictureCount > 0 ? `📷 ${pictureCount}` : '-'}</td>
                                    <td style="padding: 2px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${formatDateGlobal(invoice.created_at)}</td>
                                    <td style="padding: 2px 2px; text-align: center; color: ${pdfTextColor};">${escapeHtml(invoice.username || '-')}</td>
                                </tr>
                            `;
                        });

                        const emptyRowsNeeded = rowsPerPage - pageInvoices.length;
                        for (let i = 0; i < emptyRowsNeeded; i++) {
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${(pageInvoices.length + i) % 2 === 0 ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 2px 2px; text-align: center; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 2px 2px;">&nbsp;</td>
                                </tr>
                            `;
                        }

                        tableHTML += `
                                </tbody>
                            </table>
                        `;

                        const footerHTML = `
                            <div style="margin-top: 8px; padding-top: 5px; text-align: center;">
                                <div style="width: 30px; height: 1px; background: ${pdfBorderColor}; margin: 0 auto 5px auto;"></div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};">${pageLabel} ${pageNum} ${ofLabel} ${totalPages}</div>
                            </div>
                        `;

                        container.innerHTML = headerHTML + tableHTML + footerHTML;
                        document.body.appendChild(container);
                        html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false }).then(canvas => {
                            document.body.removeChild(container);
                            resolve(canvas);
                        }).catch(err => { document.body.removeChild(container); reject(err); });
                    } catch (err) { reject(err); }
                });
            };

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, invoicesToExport.length);
                const pageInvoices = invoicesToExport.slice(startRow, endRow);
                const canvas = await renderPage(pageInvoices, pageNum, startRow);
                if (pageNum > 1) {
                    pdf.addPage();
                }
                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);
            }

            const fileName = `Invoice_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${currentDateGlobal()}.pdf`;
            pdf.save(fileName);
            showToast('success', `${translations.reportGenerated || 'Report generated!'} ${invoicesToExport.length} ${translations.invoices || 'invoices'} on ${totalPages} ${translations.pages || 'page(s)'}`);
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredInvoices, currentLanguage, pdfOrientation, pdfPageSize, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, currentTheme]);

    const handlePageChange = useCallback((page) => { if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); }, [pagination.totalPages]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1> {translations.pendingInvoices || 'Pending Invoices'}</h1>
                            <p>{translations.formDetails || 'All pending invoices are below'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        {currentShop && (
                            <div className="ozlistui-header-search-wrapper">
                                <div className="ozlistui-floating-wrapper ozlistui-header-search">
                                    <input
                                        type="text"
                                        id="headerSearch"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                        }}
                                        placeholder=" "
                                        className="ozlistui-header-search-input"
                                    />
                                    <label htmlFor="headerSearch">
                                        <FontAwesomeIcon icon={faSearch} />
                                        {translations.searchPlaceholder || 'Search invoices...'}
                                    </label>
                                </div>
                            </div>
                        )}

                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <CustomDropdown
                            label={translations.statusFilter || 'Status Filter'}
                            icon={faTag}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={statusOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label={translations.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label={translations.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            translations={translations}
                        />

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchInvoices} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredInvoices.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{translations.loadingUserData || 'Loading your invoice data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view invoices'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Summary Cards */}
                    <div className="ozlistui-summary-cards">
                        <div className="ozlistui-summary-card">
                            <div className="ozlistui-summary-icon" style={{ background: '#3b82f620', color: '#3b82f6' }}>
                                <FontAwesomeIcon icon={faFileInvoice} />
                            </div>
                            <div className="ozlistui-summary-info">
                                <h3>{filteredInvoices.length}</h3>
                                <p>{translations.totalInvoices || 'Total Invoices'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-summary-card">
                            <div className="ozlistui-summary-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
                                <FontAwesomeIcon icon={faClock} />
                            </div>
                            <div className="ozlistui-summary-info">
                                <h3>{filteredInvoices.filter(i => i.status?.toLowerCase() === 'pending').length}</h3>
                                <p>{translations.pending || 'Pending'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-summary-card">
                            <div className="ozlistui-summary-icon" style={{ background: '#10b98120', color: '#10b981' }}>
                                <FontAwesomeIcon icon={faCheckCircle} />
                            </div>
                            <div className="ozlistui-summary-info">
                                <h3>{filteredInvoices.filter(i => i.status?.toLowerCase() === 'approved').length}</h3>
                                <p>{translations.approved || 'Approved'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-summary-card">
                            <div className="ozlistui-summary-icon" style={{ background: '#ef444420', color: '#ef4444' }}>
                                <FontAwesomeIcon icon={faTimesCircle} />
                            </div>
                            <div className="ozlistui-summary-info">
                                <h3>{filteredInvoices.filter(i => i.status?.toLowerCase() === 'rejected').length}</h3>
                                <p>{translations.rejected || 'Rejected'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="ozlistui-centered-cell">{translations.invoiceNumber || 'Invoice Number'}</th>
                                            <th className="ozlistui-centered-cell">{translations.customerName || 'Customer Name'}</th>
                                            <th className="ozlistui-centered-cell">{translations.billNumber || 'Bill Number'}</th>
                                            <th className="ozlistui-centered-cell">{translations.billPictures || 'Pictures'}</th>
                                            <th className="ozlistui-centered-cell">{translations.status || 'Status'}</th>
                                            <th className="ozlistui-centered-cell">{translations.created_at || 'Created At'}</th>
                                            <th className="ozlistui-centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentInvoices.map((invoice, index) => {
                                            const isSelfAdded = invoice.username === user?.username;
                                            const pictureCount = Array.isArray(invoice.bill_pictures) ? invoice.bill_pictures.length : 0;

                                            return (
                                                <tr key={invoice.id}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text ozlistui-invoice-number">
                                                            {invoice.invoice_number || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{invoice.customer_name || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{invoice.bill_number || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span 
                                                            className="ozlistui-cell-text ozlistui-picture-cell"
                                                            onClick={() => openPicturesInNewTab(invoice)}
                                                            style={{ 
                                                                cursor: pictureCount > 0 ? 'pointer' : 'default',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '6px',
                                                                padding: '4px 10px',
                                                                borderRadius: '6px',
                                                                background: pictureCount > 0 ? '#6366f115' : 'transparent',
                                                                transition: 'all 0.2s ease',
                                                                color: pictureCount > 0 ? '#6366f1' : '#9ca3af'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (pictureCount > 0) {
                                                                    e.currentTarget.style.background = '#6366f130';
                                                                    e.currentTarget.style.transform = 'scale(1.05)';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (pictureCount > 0) {
                                                                    e.currentTarget.style.background = '#6366f115';
                                                                    e.currentTarget.style.transform = 'scale(1)';
                                                                }
                                                            }}
                                                        >
                                                            {pictureCount > 0 ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faImage} style={{ color: '#6366f1' }} />
                                                                    {pictureCount}
                                                                    <span style={{ fontSize: '10px', color: '#6366f1', opacity: 0.7 }}>↗</span>
                                                                </>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '12px' }}>{translations.noPictures || 'No pictures'}</span>
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <StatusBadge status={invoice.status} translations={translations} />
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ fontSize: '11px' }}>{formatDate(invoice.created_at)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#010000', fontWeight: 'bold' }}>
                                                            {invoice.username || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell">
                                                        <div className="ozlistui-actions-content">
                                                            <button onClick={() => handleView(invoice)} className="ozlistui-action-btn ozlistui-view-btn" title={translations.view || 'View'}>
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button 
                                                                onClick={() => handleEdit(invoice)} 
                                                                className="ozlistui-action-btn ozlistui-edit-btn" 
                                                                title={translations.edit || 'Edit'}
                                                                disabled={!isSelfAdded}
                                                                style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredInvoices.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-left">
                                <div className="ozlistui-per-page">
                                    <span>{translations.show || 'Show'}</span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="ozlistui-per-page-select"
                                    >
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                    </select>
                                    <span>{translations.perPage || 'per page'}</span>
                                </div>
                                <div className="ozlistui-pagination-info">
                                    <span>
                                        {translations.showing || 'Showing'}
                                        <strong> {pagination.startIndex + 1} </strong>
                                        {translations.to || 'to'}
                                        <strong> {Math.min(pagination.startIndex + itemsPerPage, filteredInvoices.length)} </strong>
                                        {translations.of || 'of'}
                                        <strong> {filteredInvoices.length} </strong>
                                        {translations.invoices || 'invoices'}
                                    </span>
                                </div>
                            </div>
                            <div className="ozlistui-pagination-right">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="ozlistui-pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === pagination.totalPages}
                                    className="ozlistui-pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedInvoice && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faFileInvoice} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.invoiceDetails || 'Invoice Details'}</h3>
                                <span className="ozlistui-product-number-badge">#{selectedInvoice.id}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faFileInvoice} />{translations.invoiceDetails || 'Invoice Summary'}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.invoiceNumber || 'Invoice Number'}:</span><span className="ozlistui-view-value">{selectedInvoice.invoice_number || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.customerName || 'Customer Name'}:</span><span className="ozlistui-view-value">{selectedInvoice.customer_name || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.billNumber || 'Bill Number'}:</span><span className="ozlistui-view-value">{selectedInvoice.bill_number || '-'}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.status || 'Status'}:</span><span className="ozlistui-view-value"><StatusBadge status={selectedInvoice.status} translations={translations} /></span></div>
                                </div>
                            </div>
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faImage} />{translations.billPictures || 'Bill Pictures'}</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row">
                                        <span className="ozlistui-view-label">{translations.pictures || 'Pictures'}:</span>
                                        <span className="ozlistui-view-value">
                                            {Array.isArray(selectedInvoice.bill_pictures) && selectedInvoice.bill_pictures.length > 0 ? (
                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {selectedInvoice.bill_pictures.map((pic, idx) => (
                                                        <img 
                                                            key={idx} 
                                                            src={pic} 
                                                            alt={`Bill ${idx + 1}`} 
                                                            style={{ 
                                                                width: '80px', 
                                                                height: '80px', 
                                                                objectFit: 'cover', 
                                                                borderRadius: '8px',
                                                                border: '1px solid #e5e7eb',
                                                                cursor: 'pointer'
                                                            }} 
                                                            onClick={() => window.open(pic, '_blank')}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1.05)';
                                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.transform = 'scale(1)';
                                                                e.currentTarget.style.boxShadow = 'none';
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#9ca3af' }}>{translations.noPictures || 'No pictures'}</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-view-section ozlistui-metadata-section">
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.addedBy || 'Added By'}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge">{selectedInvoice.username || '-'}</span></span></div>
                                    {selectedInvoice.created_at && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.created_at || 'Created At'}:</span><span className="ozlistui-view-value">{formatDate(selectedInvoice.created_at)}</span></div>
                                    )}
                                    {selectedInvoice.updated_at && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.updated_at || 'Updated At'}:</span><span className="ozlistui-view-value">{formatDate(selectedInvoice.updated_at)}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            {selectedInvoice.username === user?.username && (
                                <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedInvoice); }}>
                                    <FontAwesomeIcon icon={faEdit} />
                                {translations.edit || 'Edit'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Invoice Modal */}
            {showEditModal && selectedInvoice && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.editInvoice || 'Edit Invoice'}</h3>
                                <span className="ozlistui-product-number-badge">#{selectedInvoice.id} - {selectedInvoice.invoice_number}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-form-grid">
                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            id="edit-bill-number"
                                            type="text"
                                            value={editBillNumber}
                                            onChange={(e) => setEditBillNumber(e.target.value)}
                                            placeholder=" "
                                            disabled={editLoading}
                                        />
                                        <label htmlFor="edit-bill-number">
                                            <FontAwesomeIcon icon={faTag} />
                                            {translations.billNumber || 'Bill Number'}
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <select
                                            id="edit-status"
                                            value={editStatus}
                                            onChange={(e) => setEditStatus(e.target.value)}
                                            disabled={editLoading}
                                        >
                                            <option value="pending">{translations.pending || 'Pending'}</option>
                                            <option value="approved">{translations.approved || 'Approved'}</option>
                                            <option value="rejected">{translations.rejected || 'Rejected'}</option>
                                            <option value="processing">{translations.processing || 'Processing'}</option>
                                            <option value="completed">{translations.completed || 'Completed'}</option>
                                        </select>
                                        <label htmlFor="edit-status">
                                            <FontAwesomeIcon icon={faExchangeAlt} />
                                            {translations.status || 'Status'}
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-status-preview">
                                        <div style={{ padding: '12px 0' }}>
                                            <StatusBadge status={editStatus} translations={translations} />
                                        </div>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>{translations.billPictures || 'Bill Pictures'} ({editBillPictures.length})</label>
                                    <input
                                        ref={editPicturesInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleEditPicturesUpload}
                                        style={{ display: 'none' }}
                                    />
                                    <button
                                        type="button"
                                        className="ozlistui-btn ozlistui-btn-secondary"
                                        onClick={() => editPicturesInputRef.current?.click()}
                                        disabled={editLoading}
                                    >
                                        <FontAwesomeIcon icon={faUpload} /> Add Pictures
                                    </button>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px', marginTop: '12px' }}>
                                        {editBillPictures.map((picture, index) => (
                                            <div key={`${picture.slice(0, 30)}-${index}`} style={{ position: 'relative' }}>
                                                <img
                                                    src={picture}
                                                    alt={`Bill ${index + 1}`}
                                                    title="Open pictures"
                                                    onClick={() => openPicturesInNewTab({
                                                        ...selectedInvoice,
                                                        bill_number: editBillNumber,
                                                        bill_pictures: editBillPictures
                                                    })}
                                                    style={{
                                                        width: '100%',
                                                        height: '90px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    aria-label={`Remove bill picture ${index + 1}`}
                                                    onClick={() => removeEditPicture(index)}
                                                    disabled={editLoading}
                                                    style={{ position: 'absolute', top: '4px', right: '4px' }}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.cancel || 'Cancel'}
                            </button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-primary" onClick={handleUpdateStatus} disabled={editLoading}>
                                {editLoading ? (
                                    <>
                                        <div className="ozlistui-btn-spinner"></div>
                                        <span>{translations.updating || 'Updating...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>{translations.saveChanges || 'Save Changes'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{translations.deleteMessage || 'Are you sure you want to delete this invoice?'}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingInvoicesList;
